import compose from 'recompose/compose';
import { connect } from 'react-redux';
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { canUserModifyBankAccounts } from '../../../common/helpers/users';
import { getLoansList } from '../../../loans/selectors';
import BankAccountEntity from './bankAccountEntity';
import withPermissions from '../../../auth/utils/withPermissions';
import AddBarberPrefix from '../../../common/RoleAwareLink';
import DeleteButton from '../../../common/buttons/deleteButton';
import { getIsAdminUser } from '../../../auth/selectors';
import confirmModal from '../../../common/confirmModal';
import { LOAN_STATUS } from '../../../loans/constants';

const DELETE_CONFIRMATION_MESSAGE = [
  'DO NOT delete an account if you need to ',
  'change the acct / routing numbers. Instead, use the \'Change Bank Account Number\'. ',
  'If your bank account is \'unverified\', use the edit button to provide the correct ',
  'account holder information. Administrator should only delete verified ',
  'accounts if there is a need to change the account holder ',
  'information (e.g., first name, last name and identification / SS number).',
].join('');

const ACTIVE_LOANS_DELETE_CONFIRMATION_MESSAGE = [
  'This account has an active loan in progress. Unless you have already consulted with your ' +
  'supervisor, DO NOT proceed with deletion.\n\n',
  'If you need to change the acct / routing numbers proceed to the \'Change Bank Account ' +
  'Number\' button. If your bank account is \'unverified\', use the edit button to provide the ' +
  'correct account holder information. Administrator should only delete verified accounts if ' +
  'there is a need to change the account holder information (e.g., first name, last name and ' +
  'identification / SS number), and this change has been fully vetted by a manager.\n',
  'Are you sure you want to proceed?',
].join('');

const EditButton = ({ url, label, additionalClass }) => (
  <AddBarberPrefix
    to={url}
    className={classNames('c-btn c-btn--small u-mt-', additionalClass)}
  >
    <span className="c-btn__text">{label}</span>
  </AddBarberPrefix>
);
EditButton.propTypes = {
  url: PropTypes.string.isRequired,
  label: PropTypes.string,
  additionalClass: PropTypes.string,
};

EditButton.defaultProps = {
  label: 'Edit',
  additionalClass: '',
};

class SingleBankAccount extends Component {
  constructor(props) {
    super(props);
    this.account = new BankAccountEntity(props.account);
  }

  getEditUrl(addition = '') {
    return `/settings/bank-account/${this.account.getId()}/edit${addition}`;
  }

  deleteAccount = () => {
    confirmModal(
      {
        title: 'Delete bank account',
        message: this.props.hasActiveLoan ? ACTIVE_LOANS_DELETE_CONFIRMATION_MESSAGE : DELETE_CONFIRMATION_MESSAGE,
        type: 'default',
        confirmButtonText: 'Delete',
      },
      () => this.props.deleteAccount()
    );
  };

  render() {
    const { permissions, isAdminUser } = this.props;
    const { account } = this;

    if (!account) {
      return null;
    }

    const statusClass = classNames('c-status', {
      'c-status--red': account.isUnverified(),
      'c-status--green': account.isVerified(),
      'c-status--orange': account.isPending(),
    });

    return (
      <div>
        <h5>Bank account</h5>
        <div className="o-flexbox">
          <div className="o-flexbox__item o-flexbox__item--flex-start">
            <div className="c-heading c-heading--small c-heading--semibold">
              ********{account.getExternalAccount().last4}
              ({account.getExternalAccount().bank_name})
              ({account.getFullName()})
            </div>
            <div className="c-heading c-heading--small c-heading--dark">
              {account.getFormattedAddress()}
            </div>
          </div>
          <div className="o-flexbox__item o-flexbox__item--flex-start">
            <div className={statusClass}>
              {account.getLegalEntityStatus()}
            </div>
          </div>
        </div>
        <div className="o-flexbox">
          <div className="o-flexbox__item o-flexbox__item--flex-start">
            <EditButton url={this.getEditUrl()} display-if={canUserModifyBankAccounts(permissions)} />
            <EditButton
              url={this.getEditUrl('/external')}
              label="Change bank account number"
              additionalClass="u-ml-"
              display-if={canUserModifyBankAccounts(permissions)}
            />
          </div>
          <div className="o-flexbox__item o-flexbox__item--flex-start">
            <DeleteButton
              onClick={this.deleteAccount}
              display-if={isAdminUser && canUserModifyBankAccounts(permissions)}
            />
          </div>
        </div>
      </div>
    );
  }
}
SingleBankAccount.propTypes = {
  account: PropTypes.object.isRequired,
  permissions: PropTypes.object.isRequired,
  isAdminUser: PropTypes.bool.isRequired,
  hasActiveLoan: PropTypes.bool.isRequired,
  deleteAccount: PropTypes.func.isRequired,
};

export default compose(
  withPermissions,
  connect(state => ({
    isAdminUser: getIsAdminUser(state),
    hasActiveLoan: getLoansList(state).some(loan => loan.status === LOAN_STATUS.ACCEPTED),
  }))
)(SingleBankAccount);
